import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Battery, Printer, ScanEye, Wifi, Cpu, ShieldCheck,
  Siren, ClipboardCheck, ListChecks, Play,
} from "lucide-react";
import { endpoints } from "@/lib/api";

const TABS = [
  { key: "setup", label: "Setup", icon: ClipboardCheck },
  { key: "diagnostics", label: "Diagnostics", icon: Cpu },
  { key: "incident", label: "Report Incident", icon: Siren },
  { key: "log", label: "Booth Log", icon: ListChecks },
];

export default function OperatorCompanion() {
  const [tab, setTab] = useState("setup");
  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 flex flex-col max-w-md mx-auto border-x border-zinc-200">
      <div className="tricolor-strip h-1" />
      <header className="p-4 flex items-center gap-3 border-b border-zinc-200 bg-white">
        <Link to="/" data-testid="operator-home" className="text-zinc-500"><ArrowLeft className="w-4 h-4" /></Link>
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Operator Companion</div>
          <div className="font-display font-black text-base leading-none">BTH-042 · Field App</div>
        </div>
      </header>

      <main className="flex-1 p-4 pb-24">
        {tab === "setup" && <Setup />}
        {tab === "diagnostics" && <Diagnostics />}
        {tab === "incident" && <ReportIncident />}
        {tab === "log" && <Log />}
      </main>

      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-md border-t border-zinc-200 bg-white flex">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`op-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex-1 py-3 flex flex-col items-center gap-1 text-[10px] font-mono uppercase tracking-widest ${tab === t.key ? "text-[#0A369D]" : "text-zinc-500"}`}
          >
            <t.icon className="w-4 h-4" strokeWidth={2} />{t.label}
          </button>
        ))}
      </nav>
    </div>
  );
}

function Setup() {
  const steps = [
    { t: "Verify tamper seals intact", d: "Inspect all 4 enclosure seals for damage or tampering evidence." },
    { t: "Confirm device certificate", d: "Read the on-device fingerprint aloud to the observer for cross-verification." },
    { t: "Load signed electoral-roll snapshot", d: "Insert USB with today's signed snapshot; wait for the green checksum match." },
    { t: "Test print a null VVPAT slip", d: "Discard the null slip; the officer and observer both initial the discard log." },
    { t: "Perform mock authentication", d: "Run one dry-run retina scan and one fallback drill." },
    { t: "Officer + observer co-sign booth open", d: "Both enter PINs on the kiosk. Booth is now polling-ready." },
  ];
  const [done, setDone] = useState({});
  const completed = Object.values(done).filter(Boolean).length;
  return (
    <div className="space-y-4">
      <div className="border border-zinc-200 bg-white p-4">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Pre-open checklist</div>
        <div className="font-display font-black text-2xl">{completed}/{steps.length} complete</div>
        <div className="mt-3 h-1.5 border border-zinc-200"><motion.div style={{ background: "#138808", height: "100%" }} animate={{ width: `${(completed / steps.length) * 100}%` }} /></div>
      </div>
      <div className="space-y-3">
        {steps.map((s, i) => (
          <button
            key={i}
            data-testid={`setup-step-${i}`}
            onClick={() => setDone({ ...done, [i]: !done[i] })}
            className={`w-full text-left border p-4 flex gap-3 items-start ${done[i] ? "border-[#138808] bg-[#f0fbf3]" : "border-zinc-200 bg-white"}`}
          >
            <div className={`w-6 h-6 border-2 mt-0.5 shrink-0 grid place-items-center ${done[i] ? "border-[#138808] bg-[#138808] text-white" : "border-zinc-400"}`}>{done[i] ? "✓" : ""}</div>
            <div>
              <div className="font-medium">{s.t}</div>
              <div className="text-xs text-zinc-600 mt-0.5">{s.d}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function Diagnostics() {
  const [readings, setReadings] = useState({ battery: 84, printer: "READY", scanner: "READY", uplink: "OFFLINE-OK", secureBoot: "OK" });
  const [running, setRunning] = useState(false);
  async function runSelfTest() {
    setRunning(true);
    for (const key of ["battery", "printer", "scanner", "uplink", "secureBoot"]) {
      await new Promise((r) => setTimeout(r, 400));
      setReadings((prev) => ({ ...prev, [key]: key === "battery" ? Math.max(70, prev.battery - Math.floor(Math.random() * 3)) : prev[key] }));
    }
    setRunning(false);
    toast.success("Self-test passed · device green");
  }
  const rows = [
    { icon: Battery, label: "Battery", value: `${readings.battery}%`, ok: readings.battery > 30 },
    { icon: Printer, label: "VVPAT Printer", value: readings.printer, ok: readings.printer === "READY" },
    { icon: ScanEye, label: "Retina Sensor", value: readings.scanner, ok: readings.scanner === "READY" },
    { icon: Wifi, label: "Uplink", value: readings.uplink, ok: true },
    { icon: ShieldCheck, label: "Secure Boot", value: readings.secureBoot, ok: readings.secureBoot === "OK" },
  ];
  return (
    <div className="space-y-4">
      <div className="border border-zinc-200 bg-white">
        {rows.map((r) => (
          <div key={r.label} className="p-4 border-b border-zinc-100 flex items-center gap-4">
            <r.icon className="w-5 h-5" style={{ color: r.ok ? "#138808" : "#DC2626" }} strokeWidth={2} />
            <div className="flex-1">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{r.label}</div>
              <div className="font-display font-bold">{r.value}</div>
            </div>
            <span className={`text-[10px] font-mono ${r.ok ? "text-[#138808]" : "text-[#DC2626]"}`}>{r.ok ? "OK" : "CHECK"}</span>
          </div>
        ))}
      </div>
      <button
        data-testid="btn-self-test"
        disabled={running}
        onClick={runSelfTest}
        className="w-full bg-[#0A369D] text-white py-4 font-medium disabled:opacity-60 inline-flex items-center justify-center gap-2"
      >
        <Play className="w-4 h-4" />{running ? "Running self-test…" : "Run device self-test"}
      </button>
    </div>
  );
}

function ReportIncident() {
  const [form, setForm] = useState({ kind: "hardware.printer", severity: "medium", description: "" });
  const [submitting, setSubmitting] = useState(false);
  async function submit(e) {
    e.preventDefault();
    if (!form.description.trim()) return toast.error("Describe the incident");
    setSubmitting(true);
    try {
      await endpoints.createIncident({ booth_id: "BTH-042", reported_by: "Operator Companion", ...form });
      toast.success("Incident filed to ECI portal");
      setForm({ ...form, description: "" });
    } catch { toast.error("Failed to send — will retry when online"); }
    setSubmitting(false);
  }
  return (
    <form onSubmit={submit} className="space-y-3 bg-white border border-zinc-200 p-4" data-testid="incident-form">
      <div>
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Kind</label>
        <select data-testid="incident-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm">
          <option value="hardware.printer">VVPAT printer fault</option>
          <option value="hardware.scanner">Retina scanner fault</option>
          <option value="voter.dispute">Voter dispute</option>
          <option value="crowd.security">Crowd / security</option>
          <option value="power.loss">Power loss</option>
          <option value="other">Other</option>
        </select>
      </div>
      <div>
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Severity</label>
        <div className="grid grid-cols-4 gap-2 mt-1">
          {["low", "medium", "high", "critical"].map((s) => (
            <button type="button" key={s} data-testid={`sev-${s}`} onClick={() => setForm({ ...form, severity: s })} className={`py-2 text-xs font-mono uppercase tracking-widest border ${form.severity === s ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-300"}`}>{s}</button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Description</label>
        <textarea data-testid="incident-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 text-sm" placeholder="What happened, when, who was involved?" />
      </div>
      <button data-testid="btn-file-incident" disabled={submitting} className="w-full bg-[#DC2626] text-white py-3 font-medium disabled:opacity-60">{submitting ? "Filing…" : "File incident"}</button>
    </form>
  );
}

function Log() {
  const [events, setEvents] = useState([]);
  useEffect(() => { endpoints.events("BTH-042").then(setEvents).catch(() => {}); }, []);
  return (
    <div className="bg-white border border-zinc-200 divide-y divide-zinc-100">
      {events.length === 0 && <div className="p-6 text-sm text-zinc-500">No booth events yet. Ask the kiosk to Push Sync.</div>}
      {events.map((e) => (
        <div key={e.event_id} className="p-3 flex items-baseline justify-between">
          <div className="text-xs">
            <div className="font-mono">{e.kind}</div>
            <div className="text-[10px] text-zinc-500 font-mono">{e.hash.slice(0, 20)}…</div>
          </div>
          <div className="text-[10px] font-mono text-zinc-500">{new Date(e.timestamp).toLocaleTimeString()}</div>
        </div>
      ))}
    </div>
  );
}
